import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useWizardDraft – Auto-Save & Resume für geführte Assistenten
 * Speichert den Fortschritt in localStorage, bietet Resume-Dialog.
 */
export function useWizardDraft(key, initialData = {}) {
  const storageKey = `foresthub_draft_${key}`;
  const [hasDraft, setHasDraft] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const saveTimerRef = useRef(null);

  // Check for existing draft on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Only show resume if draft has meaningful data
        if (parsed && parsed._step > 1) {
          setHasDraft(true);
        }
      }
    } catch { /* ignore */ }
    setDraftLoaded(true);
  }, [storageKey]);

  const saveDraft = useCallback((data) => {
    // Debounce saves
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(storageKey, JSON.stringify({
          ...data,
          _savedAt: new Date().toISOString(),
        }));
      } catch { /* storage full – ignore */ }
    }, 500);
  }, [storageKey]);

  const loadDraft = useCallback(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        setHasDraft(false);
        return parsed;
      }
    } catch { /* ignore */ }
    return null;
  }, [storageKey]);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(storageKey);
    setHasDraft(false);
  }, [storageKey]);

  const dismissDraft = useCallback(() => {
    clearDraft();
  }, [clearDraft]);

  return { hasDraft, draftLoaded, saveDraft, loadDraft, clearDraft, dismissDraft };
}
